import useAppModal from 'hooks/useAppModal';
import pageChannels from 'i18n/resources/pageChannels/en.json';
import {
  Integration,
  IntegrationCategory,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChannelDescriptionModalWrapper } from './ChannelDescriptionModal.styles';
import {
  CHANNEL_NAME_LINKS,
  CHANNEL_SIGNUP_LINKS,
} from './channelDescription.constants';

type MappedChannelsDescriptionsEnum =
  keyof typeof pageChannels.channelsDescriptions;
type MappedChannelsSignupLinksEnum = keyof typeof CHANNEL_SIGNUP_LINKS;
type MappedChannelsNameLinksEnum = keyof typeof CHANNEL_NAME_LINKS;

const useChannelDescriptionModal = () => {
  const { openConfirmModal } = useAppModal();
  const { t } = useTranslation();

  const getModalBody = ({ integration }: { integration: Integration }) => {
    const channelNameLink =
      CHANNEL_NAME_LINKS[integration.enumId as MappedChannelsNameLinksEnum];
    const signupLink =
      CHANNEL_SIGNUP_LINKS[integration.enumId as MappedChannelsSignupLinksEnum];
    const shouldDisplayDistributionGeography =
      integration.category === IntegrationCategory.DIRECT_CHANNEL;

    return (
      <ChannelDescriptionModalWrapper>
        <p>
          <Trans
            i18nKey={`pageChannels.channelsDescriptions.${
              integration?.enumId as MappedChannelsDescriptionsEnum
            }`}
            components={{
              bold: <b />,
              p: <p />,
              nameLink: (
                <Link
                  to={channelNameLink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Channel link"
                />
              ),
              signUpLink: (
                <Link
                  to={signupLink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Signup link"
                />
              ),
            }}
          >
            {t('common.notApplicable')}
          </Trans>
        </p>

        {shouldDisplayDistributionGeography && (
          <p>
            <Trans
              i18nKey="pageChannels.modals.viewChannelDescription.distributionGeography"
              components={{ 1: <b /> }}
              values={{
                distributionGeography: integration.distributionGeography,
              }}
            />
          </p>
        )}
      </ChannelDescriptionModalWrapper>
    );
  };

  const openChannelDescriptionModal = ({
    integration,
  }: InternalIntegrationSetting) => {
    openConfirmModal({
      title: t('pageChannels.modals.viewChannelDescription.title', {
        channel: integration.displayName ?? integration.name,
      }),
      body: getModalBody({
        integration,
      }),
      onConfirm: () => {},
      confirmLabel: t('pageChannels.modals.viewChannelDescription.confirmBtn'),
      hideCancel: true,
    });
  };

  return { openChannelDescriptionModal };
};

export default useChannelDescriptionModal;
